/**
 * Pre-test Analysis Component - Test planlama ve sample size hesaplamaları
 * 
 * Features:
 * - Sample size calculation
 * - Test duration calculation
 * - MDE calculation
 * - Statistical Power analysis
 */

import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Paper,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  Science as ScienceIcon,
  EventNote as PlanIcon,
} from '@mui/icons-material';
import {
  formatNumber,
  formatPercentage,
} from '../../utils/statisticsUtils';
import PreTestResultsCharts from './PreTestResultsCharts';
import { BRAND } from '../../theme/brandTheme';

const PreTestAnalysis = ({
  preTestData,
  preTestResults,
  loading,
  onPreTestChange,
  onCalculate,
  onClear,
  onExport,
  showSampleButton,
  onLoadSampleData,
  showPlanButton,
  onPlanTest,
}) => {
  return (
    <Grid container spacing={3}>
      {/* Input Section */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.purple }}>
                Test Parametreleri
              </Typography>
              {showSampleButton && (
                <Button
                  variant="contained"
                  onClick={onLoadSampleData}
                  disabled={loading}
                  startIcon={<ScienceIcon />}
                  size="small"
                  sx={{
                    bgcolor: BRAND.green,
                    boxShadow: 'none',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#089a1f', boxShadow: 'none' },
                  }}
                >
                  Örnek Veriler
                </Button>
              )}
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Baseline Conversion Rate (%)"
                  type="number"
                  value={preTestData.baselineRate}
                  onChange={(e) => onPreTestChange('baselineRate', e.target.value)}
                  inputProps={{ min: 0.1, max: 100, step: 0.1 }}
                  helperText="Mevcut dönüşüm oranınız"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Minimum Detectable Effect (%)"
                  type="number"
                  value={preTestData.minDetectableEffect}
                  onChange={(e) => onPreTestChange('minDetectableEffect', e.target.value)}
                  inputProps={{ min: 1, max: 100, step: 1 }}
                  helperText="Tespit etmek istediğiniz minimum iyileştirme"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Güven Seviyesi</InputLabel>
                  <Select
                    value={preTestData.alpha}
                    label="Güven Seviyesi"
                    onChange={(e) => onPreTestChange('alpha', e.target.value)}
                  >
                    <MenuItem value={0.1}>90%</MenuItem>
                    <MenuItem value={0.05}>95%</MenuItem>
                    <MenuItem value={0.01}>99%</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Statistical Power</InputLabel>
                  <Select
                    value={preTestData.power}
                    label="Statistical Power"
                    onChange={(e) => onPreTestChange('power', e.target.value)}
                  >
                    <MenuItem value={0.7}>70%</MenuItem>
                    <MenuItem value={0.8}>80%</MenuItem>
                    <MenuItem value={0.9}>90%</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Günlük Trafik"
                  type="number"
                  value={preTestData.dailyTraffic}
                  onChange={(e) => onPreTestChange('dailyTraffic', e.target.value)}
                  inputProps={{ min: 100, step: 100 }}
                  helperText="Günlük ziyaretçi sayısı"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Trafik Dağılımı (%)"
                  type="number"
                  value={preTestData.trafficSplit}
                  onChange={(e) => onPreTestChange('trafficSplit', e.target.value)}
                  inputProps={{ min: 10, max: 90, step: 10 }}
                  helperText="Teste yönlendirilecek trafik yüzdesi"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={onCalculate}
                disabled={loading}
                startIcon={<CalculateIcon />}
              >
                {loading ? 'Hesaplanıyor...' : 'Hesapla'}
              </Button>
              <Button
                variant="outlined"
                onClick={onClear}
                startIcon={<RefreshIcon />}
              >
                Temizle
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Results Section */}
      <Grid item xs={12} md={6}>
        {preTestResults && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Planlama Sonuçları
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {formatNumber(preTestResults.sampleSize)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam Sample Size
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {formatNumber(preTestResults.sampleSizePerVariant)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Varyant Başına
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="secondary">
                      {preTestResults.duration.durationDays}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Test Süresi (Gün)
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="secondary">
                      {preTestResults.duration.durationWeeks}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Test Süresi (Hafta)
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>MDE:</strong> {formatPercentage(preTestResults.mde.relativeMDE / 100)} relative lift<br/>
                      <strong>Statistical Power:</strong> {formatPercentage(preTestResults.power / 100)}<br/>
                      <strong>Confidence Level:</strong> {formatPercentage(preTestResults.confidenceLevel / 100)}
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>

              <PreTestResultsCharts preTestResults={preTestResults} />

              <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
                {showPlanButton && (
                  <Button
                    variant="contained"
                    onClick={onPlanTest}
                    startIcon={<PlanIcon />}
                    fullWidth
                    sx={{
                      bgcolor: BRAND.purple,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { bgcolor: '#3d1c6b' },
                    }}
                  >
                    Test Planla — Taslak Oluştur
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={onExport}
                  startIcon={<ExportIcon />}
                  fullWidth
                >
                  Sonuçları Dışa Aktar
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
        
        {loading && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Hesaplanıyor...
              </Typography>
              <LinearProgress />
            </CardContent>
          </Card>
        )}
      </Grid>
    </Grid>
  );
};

export default PreTestAnalysis; 